import { Table, TableBody, TableCell, TableHead, TableRow, Paper, Tooltip, AlertColor } from '@mui/material';
import { Scorecard, UserMinimal } from 'src/Constants/Types';
import { colorDarkSuccess, colorDarkFailure, colorLightSuccess, colorLightFailure } from 'src/GlobalStyles/MuiTheme';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const MAX_QUIZ_TEXT_LENGTH = 20;

const QuizTable = ({ quizScoreData, sessionStudents, onNotification }: {
  quizScoreData: Scorecard[] | null,
  sessionStudents: UserMinimal[] | null,
  onNotification: (message: string, notificationType?: AlertColor) => void,
}) => {

  // Convert to format the quiz text stored in the quizScoreData.questionScorecards array.
  // Prepend a "Q#" to the front and increment the number for each question.
  const getTableFormattedQuizName = (quizQuestion: { questionText: string }, index: number) => {
    const questionNumber = `Q${index + 1}`;
    const trimmedQuestionText = quizQuestion.questionText.trim();

    if (trimmedQuestionText.length > MAX_QUIZ_TEXT_LENGTH) {
      // split into two rows, end with ellipsis
      return (
        <div style={{
          width: '90px',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          {questionNumber}: {trimmedQuestionText.substring(0, MAX_QUIZ_TEXT_LENGTH / 2)}<br />{trimmedQuestionText.substring(MAX_QUIZ_TEXT_LENGTH / 2, MAX_QUIZ_TEXT_LENGTH)}...
        </div>
      );
    } else {
      // use as is
      return (
        <div style={{
          width: '90px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          wordWrap: 'break-word'
        }}>
          {questionNumber}: {trimmedQuestionText}
        </div>
      );
    }
  }

  // Display loading message if data is not loaded
  if (!quizScoreData || quizScoreData.length <= 0) {
    return <div>Loading Quiz Data...</div>;
  }

  const bestScorecards = quizScoreData.reduce<Record<string, Scorecard>>((acc, sc) => {
    const existing = acc[sc.userId];
    if (!existing || sc.questionsScore > existing.questionsScore ||
      (sc.questionsScore === existing.questionsScore && sc.time > existing.time)) {
      acc[sc.userId] = sc;
    }
    return acc;
  }, {});

  return (
    <Paper elevation={3} style={{ width: 'fit-content', border: '1px solid black', }}>
      <Table size="small">
        <TableHead>
          {/* Quiz Question Row */}
          <TableRow>
            <TableCell style={{ border: '1px solid black', minWidth: "120px" }}>User Name</TableCell>
            {quizScoreData[0].questionScorecards.map((quizQuestion, index) => (
              <TableCell key={`${quizScoreData[0].scenarioId}-${index}`}
                style={{ border: '1px solid black', textAlign: 'center', paddingLeft: 8, paddingRight: 8, paddingTop: 8, paddingBottom: 8 }}>
                <Tooltip title={quizQuestion.questionText} arrow>
                  <span>{getTableFormattedQuizName(quizQuestion, index)}</span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sessionStudents?.map(user => {
            const scorecard = bestScorecards[user.id]; // only best attempt
            return (
              <TableRow key={user.id}>
                <TableCell style={{ border: '1px solid black' }}>{user.username}</TableCell>
                {scorecard
                  ? scorecard.questionScorecards.map((qScorecard, qIndex) => {
                    const answeredLetters = qScorecard.answerScorecards
                      .filter(a => a.answered)
                      .map((a, i) => {
                        const isCorrect = a.answered === a.correct;
                        const cellColor = qScorecard.maxScore !== 0
                          ? (isCorrect ? colorLightSuccess : colorLightFailure)
                          : 'inherit';
                        return (
                          <Tooltip key={i} title={a.answerText || ''} arrow>
                            <span style={{ color: cellColor }}>
                              {qScorecard.maxScore !== 0 ? (
                                isCorrect ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />
                              ) : (
                                String.fromCharCode(65 + i)
                              )}
                            </span>
                          </Tooltip>
                        );
                      });

                    const bgColor = qScorecard.maxScore !== 0
                      ? (qScorecard.maxScore === qScorecard.score ? colorDarkSuccess : colorDarkFailure)
                      : 'inherit';

                    return (
                      <TableCell key={qIndex} style={{ border: '1px solid black', backgroundColor: bgColor, textAlign: 'center' }}>
                        {answeredLetters.reduce<JSX.Element | null>((prev, curr) => prev ? <>{prev}, {curr}</> : <>{curr}</>, null)}
                      </TableCell>
                    );
                  })
                  : quizScoreData[0].questionScorecards.map((_, qIndex) => (
                    <TableCell key={qIndex} style={{ border: '1px solid black', textAlign: 'center' }}>-</TableCell>
                  ))
                }
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default QuizTable;